<script>
    import * as Yup from "yup";       
    import { useValidations } from "../../../../../SharedSvelte/Forms.svelte";
    import { FormGroup, Label, Input, InputGroup, Alert, Card, CardHeader, CardBody, CardText } from "@sveltestrap/sveltestrap";
    import { appProfileState } from "../../state/ApplicationState.svelte";      
    import { AddressTypes } from "./AddressInput.svelte";
    import AddressInput from "./AddressInput.svelte";
    import { PhoneTypes } from "./PhoneNumberInput.svelte";
    import PhoneNumberInput from "./PhoneNumberInput.svelte";
    import { Loader } from "../../../../../SharedSvelte/GlobalLoader.svelte";
	import { baseUrl } from "../../../../../SharedSvelte/Common.svelte";	
	import Dialog from "../../../../../SharedSvelte/Dialog.svelte";

    const { validateField, validateForm, getErrors, resetErrors, removeError } = useValidations();	

    let formData = $state({
	    email: appProfileState.memberDetails?.email,
		preferredContactMethod: appProfileState.memberDetails?.contactMethod,
		documentDeliveryMethod: appProfileState.memberDetails?.documentDeliveryMethod,        
        isPHIRestricted: appProfileState.memberDetails?.isPHIRestricted		
    });   
	   	
	let saveError = $state("");	
    let errors = $derived(getErrors());
	let formHasErrors = $state(false);   	
	let prompt;

    const formSchema = Yup.object({
        email: Yup.string()
			.required("Email is required")
			.email("Invalid Email address"),
		preferredContactMethod: Yup.string()
			.required("Preferred Contact Method is required"),
		documentDeliveryMethod: Yup.string()
			.required("Document Delivery Method is required")		
	});    

    function handleEmailChange(event) {
		validateField(formSchema, "email", formData);
	}

	function handlePreferredContactMethodChange(event) {
		validateField(formSchema, "preferredContactMethod", formData);
	}

	function handleDocumentDeliveryMethodChange(event) {
		validateField(formSchema, "documentDeliveryMethod", formData);
	}      
	
    //*** Address and Phone Components
    let residentialAddressInput;
	let mailingAddressInput;
    let homePhoneNumberInput;
	let cellPhoneNumberInput;

    let cellOrHomePhoneError = $state("");    
    let residentialInitialValues = 
			appProfileState.memberDetails?.residentialAddress1+
			appProfileState.memberDetails?.residentialAddress2+
			appProfileState.memberDetails?.residentialCity+
			appProfileState.memberDetails?.residentialState+
			appProfileState.memberDetails?.residentialZip;
	let mailingInitialValues = 
			appProfileState.memberDetails?.mailingAddress1+
			appProfileState.memberDetails?.mailingAddress2+
			appProfileState.memberDetails?.mailingCity+
			appProfileState.memberDetails?.mailingState+
			appProfileState.memberDetails?.mailingZip;
	let isSameAsResidential = $state(residentialInitialValues === mailingInitialValues);  

    $effect(() => {
		formHasErrors = 
			Object.keys(errors).length 
			|| residentialAddressInput.hasErrors() 
			|| mailingAddressInput.hasErrors() 
			|| cellPhoneNumberInput.hasErrors() 
			|| homePhoneNumberInput.hasErrors()			
			|| cellOrHomePhoneError ? true : false;    

		if (isSameAsResidential) {
			mailingAddressInput.setValues({ ...residentialAddressInput.getValues() });
		}
		mailingAddressInput.disableInputs(isSameAsResidential)

		if (homePhoneNumberInput.getValues()?.phoneNumber || cellPhoneNumberInput.getValues()?.phoneNumber){
			cellOrHomePhoneError = "";
		}		
	});   

    async function validateCellOrHomePhoneIsPresent()
	{
		const homeValue = (await homePhoneNumberInput.getValues())?.phoneNumber;
		const cellValue = (await cellPhoneNumberInput.getValues())?.phoneNumber;
		if (!homeValue && !cellValue) {
			cellOrHomePhoneError = "Cell or Home phone number is required";
		} else cellOrHomePhoneError = "";
	}   
	//***/

    async function savePersonalInfo() {
        await validateForm(formSchema, formData);
		await residentialAddressInput.validate();
		await mailingAddressInput.validate();
		await homePhoneNumberInput.validate();
		await cellPhoneNumberInput.validate();
		await validateCellOrHomePhoneIsPresent();       
		if (formHasErrors) return;

		await residentialAddressInput.verify();
		if (!isSameAsResidential) await mailingAddressInput.verify();
		
        Loader.display();

        try {
            var sendFormData = JSON.parse(JSON.stringify(formData));			
			sendFormData.residentialAddress = residentialAddressInput.getValues();
			sendFormData.mailingAddress = mailingAddressInput.getValues();
			sendFormData.homePhone = homePhoneNumberInput.getValues()?.phoneNumber;
			sendFormData.cellPhone = cellPhoneNumberInput.getValues()?.phoneNumber;			
			
			// console.log(sendFormData);

             const response = await fetch(`${baseUrl}Options/Profile/SavePersonalInfo`, {
				    method: "POST",
				    headers: {
					    "Content-Type": "application/json",
					    __RequestVerificationToken: document.getElementsByName("__RequestVerificationToken")[0].value,
				    },
				    body: JSON.stringify(sendFormData),
			    });
			    if (!response.ok) {
				    const error = `Server exception: ${response.status} ${response.statusText}`;
				    console.log(error);
				    throw new Error();
			    }

			    const json = await response.json();
			    if (!json.success) {
				    console.log(json.errors);
				    throw new Error(json.errors);
			    }
				
			appProfileState.memberDetails = json.memberDetails;

			prompt.showMessage(
				"Your request has been received. Please allow 15 minutes to see your changes.", 
				"Success", 
				()=> {					
					appProfileState.isEditorMode = false;                    
					clearAllErrors();
					window.scrollTo(0, 0);
			});            			
        }
        catch (err) {           
            saveError = "Failed to save personal info. ";
            if (err.message) saveError += err.message;           
        }
        finally {
            Loader.hide();
        }       
    }

	function clearAllErrors() {
		resetErrors();
		saveError = "";
	}

    function cancelUpdate() {	       
        appProfileState.isEditorMode = false;        
        window.scrollTo(0, 0);		
	} 

    function handleSubmit(event) {
		event.preventDefault();
		savePersonalInfo();
	}
    
</script>

<div class="mb-3">
    <form onsubmit={handleSubmit} novalidate> 
        <div class="row mt-2 mb-2">
	        <div class="col-xs-12 col-sm-12 col-md-12 col-lg-6 pt-4 mt-2 mb-3">	
                <AddressInput
			        bind:this={residentialAddressInput}
				    addressType={AddressTypes.Residential}
				    address1={appProfileState.memberDetails?.residentialAddress1}
				    address2={appProfileState.memberDetails?.residentialAddress2}
				    city={appProfileState.memberDetails?.residentialCity}
				    usState={appProfileState.memberDetails?.residentialState}
				    zip={appProfileState.memberDetails?.residentialZip}
	            />
            </div>
		    <div class="col-xs-12 col-sm-12 col-md-12 col-lg-6 pt-1 mb-0">
			    <Input                 
			        type="checkbox" 
				    label="Mailing is same as Residential" 
				    bind:checked={isSameAsResidential}
					name="mailing-residential"
		        />
				<AddressInput
			        bind:this={mailingAddressInput}
				    addressType={AddressTypes.Mailing}
					address1={appProfileState.memberDetails?.mailingAddress1}
				    address2={appProfileState.memberDetails?.mailingAddress2}
				    city={appProfileState.memberDetails?.mailingCity}
				    usState={appProfileState.memberDetails?.mailingState}
				    zip={appProfileState.memberDetails?.mailingZip}
				    disabled={isSameAsResidential}
			     />
				 
	        </div>
        </div>

        <Card class="mt-3">
            <CardHeader>Contact Information</CardHeader>
				<CardBody>   
					<CardText>     
						<div class="row">                                  
							<div class="col-xs-12 col-sm-12 col-md-4 col-lg-4">
								<PhoneNumberInput
									bind:this={cellPhoneNumberInput}
									phoneType={PhoneTypes.Cell}
									phoneNumber={appProfileState.memberDetails?.cellPhone}
								/>
								{#if cellOrHomePhoneError}
									<p class="text-danger" role="alert">{cellOrHomePhoneError}</p>
								{/if}
							</div>
							<div class="col-xs-12 col-sm-12 col-md-4 col-lg-4">
								<PhoneNumberInput
									bind:this={homePhoneNumberInput}
									phoneType={PhoneTypes.Home}
									phoneNumber={appProfileState.memberDetails?.homePhone}
								/>
							</div>
							<div class="col-xs-12 col-sm-12 col-md-4 col-lg-4">
								<FormGroup floating label="Email">            
									<Input                   
										placeholder="Email"
										type="email"
										bind:value={formData.email}     
										onchange={handleEmailChange}
										invalid={Boolean(errors.email)}
										feedback={errors.email}
									/>
								</FormGroup>
							</div>
						</div>
					</CardText>  
	        </CardBody>  
        </Card>
    
        <Card class="mt-3">
			<CardHeader>
				Communication Preferences
			</CardHeader>
			<CardBody>   				
				<CardText>
					<div class="row">
						<div class="col-xs-12 col-sm-12 col-md-6 col-lg-6">
							<FormGroup floating spacing="mb-3" label="Preferred Contact Method">
								<Input
									type="select"
									bind:value={formData.preferredContactMethod}
									invalid={Boolean(errors.preferredContactMethod)}
									feedback={errors.preferredContactMethod}
									onchange={handlePreferredContactMethodChange}
								>									
									<option value=""></option>
									{#each appProfileState.contactMethods as contactMethod}
										<option value={contactMethod.code}>{contactMethod.text}</option>
									{/each}
								</Input>
							</FormGroup>
						</div>
						<div class="col-xs-12 col-sm-12 col-md-6 col-lg-6">
							<FormGroup floating spacing="mb-3" label="Document Delivery">
								<Input
									type="select"
									bind:value={formData.documentDeliveryMethod}
									invalid={Boolean(errors.documentDeliveryMethod)}
									feedback={errors.documentDeliveryMethod}
									onchange={handleDocumentDeliveryMethodChange}
								>
									<option value=""></option>
									{#each appProfileState.documentDeliveryMethods as deliveryMethod}
										<option value={deliveryMethod.code}>{deliveryMethod.text}</option>
									{/each}
								</Input>
							</FormGroup>		
						</div>
					</div>													
				 </CardText>  
			</CardBody>  
		</Card>

		 {#if !appProfileState.isUserSubscriber}
			<Card class="mt-3">
				<CardHeader>
					Personal Health Information Viewing Privacy
				</CardHeader>              
				<CardBody>  
					<p>
						This section allows you to grant the subscriber (the policy holder whose plan you are on) a permission to view or 
						not to view your personal health information (e.g., your claims, letters, address, benefit limits).
					</p>
					<CardText>     
						<p>
							<FormGroup>
								<Input 
									type="radio" 
									value={false}
									label="Allow the subscriber to view my personal health information"
									bind:group={formData.isPHIRestricted}									
								 />                                                                 
							</FormGroup>
						</p>  
						<p>
							<FormGroup>
								<Input                             
									type="radio" 
									value={true}
									label="Do not allow the subscriber to view my personal health information"
									bind:group={formData.isPHIRestricted}									
								/>                                                                 
							</FormGroup> 
						</p>
					</CardText>  
				</CardBody>  
			</Card>
		 {/if}	     		 		
    
		<div>
			{#if saveError}
				<p class="text-danger mt-2" role="alert">{saveError}</p>
			{/if}
		</div>
		<div class="button-group mt-3">
				<button type="button" class="btn btn-secondary btn-lg me-3" onclick={cancelUpdate}>Cancel</button>
				<button type="submit" class="btn btn-primary btn-lg" disabled={formHasErrors}>Save</button>
		</div>
       
    </form>    
</div>

<Dialog bind:this={prompt} />